import * as React from 'react'
import { shopify } from '../utils/shopify-client'

const defaultValues = {
    cart: null,
    loading: false,
    addVariantToCart: () => { },
    removeLineItem: () => { },
    updateLineItem: () => { },
    hiddenIds: [],
    setHiddenIds: () => { },
}

export const StoreContext = React.createContext(defaultValues)

const isBrowser = typeof window !== 'undefined'
const localStorageKey = 'shopify_cart_id'

export const StoreProvider = ({ children }) => {
    const [cart, setCart] = React.useState(null)
    const [loading, setLoading] = React.useState(false)
    const [hiddenIds, setHIds] = React.useState([])

    const setCartItem = (cartData) => {
        if (!cartData?.id) {
            console.warn('Invalid cart data:', cartData)
            return
        }

        if (isBrowser) {
            localStorage.setItem(localStorageKey, cartData.id)
        }

        setCart(cartData)
    }

    const createCart = async () => {
        try {
            const response = await shopify.request(`
                      mutation {
                        cartCreate {
                          cart {
                            id
                            checkoutUrl
                            cost {
                                subtotalAmount {
                                  amount
                                  currencyCode
                                }
                                totalAmount {
                                  amount
                                  currencyCode
                                }
                              }
                            lines(first: 100) {
                              edges {
                                node {
                                  id
                                  quantity
                                  cost {
                                    amountPerQuantity {
                                      amount
                                      currencyCode
                                    }
                                    totalAmount {
                                      amount
                                      currencyCode
                                    }
                                  }
                                  merchandise {
                                    ... on ProductVariant {
                                      id
                                      title
                                      priceV2 {
                                            amount
                                            currencyCode
                                      }
                                      image {
                                          originalSrc
                                          altText
                                      }
                                      product {
                                          title
                                          handle
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    `)

            const cart = response?.data?.cartCreate?.cart
            return cart
        } catch (err) {
            console.error('createCart failed:', err)
            return null
        }
    }


    const fetchCart = async (cartId) => {
        const response = await shopify.request(`
                    query {
                      cart(id: "${cartId}") {
                        id
                        checkoutUrl
                        cost {
                            subtotalAmount {
                              amount
                              currencyCode
                            }
                            totalAmount {
                              amount
                              currencyCode
                            }
                          }
                        lines(first: 100) {
                          edges {
                            node {
                              id
                              quantity
                             cost {
                                amountPerQuantity {
                                  amount
                                  currencyCode
                                }
                                totalAmount {
                                  amount
                                  currencyCode
                                }
                              }
                              merchandise {
                                ... on ProductVariant {
                                  id
                                  title
                                  priceV2 {
                                        amount
                                        currencyCode
                                  }
                                  image {
                                        originalSrc
                                        altText
                                  }
                                  product {
                                        title
                                        handle
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  `)

        return response?.data?.cart
    }


    React.useEffect(() => {
        const initializeCart = async () => {
            const existingCartId = isBrowser ? localStorage.getItem(localStorageKey) : null

            if (existingCartId && existingCartId !== 'null') {
                try {
                    const existingCart = await fetchCart(existingCartId)
                    if (existingCart) {
                        setCartItem(existingCart)
                        return
                    }
                } catch {
                    localStorage.removeItem(localStorageKey)
                }
            }
            const newCart = await createCart()
            setCartItem(newCart)
        }

        initializeCart()
    }, [])

    const addVariantToCart = async (variantId, quantity) => {
        setLoading(true)
        try {
            let activeCart = cart
            if (!activeCart) {
                console.log('No cart found, creating one...')
                const newCart = await createCart()

                if (!newCart?.id) {
                    throw new Error('Cart creation failed: no ID returned')
                }

                setCartItem(newCart)
                activeCart = newCart
            }

            const response = await shopify.request(`
                        mutation {
                          cartLinesAdd(cartId: "${activeCart.id}", lines: [{
                            merchandiseId: "${variantId}",
                            quantity: ${parseInt(quantity, 10)}
                          }]) {
                            cart {
                              id
                              checkoutUrl
                              cost {
                                        subtotalAmount {
                                          amount
                                          currencyCode
                                        }
                                        totalAmount {
                                          amount
                                          currencyCode
                                        }
                                      }
                              lines(first: 100) {
                                edges {
                                  node {
                                    id
                                    quantity
                                    cost {
                                        amountPerQuantity {
                                          amount
                                          currencyCode
                                        }
                                        totalAmount {
                                          amount
                                          currencyCode
                                        }
                                      }
                    
                                    merchandise {
                                      ... on ProductVariant {
                                        id
                                        title
                                        priceV2 {
                                            amount
                                            currencyCode
                                        }
                                        image {
                                          originalSrc
                                          altText
                                        }
                                        product {
                                          title
                                          handle
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      `)

            const updatedCart = response?.data?.cartLinesAdd?.cart
            if (updatedCart) setCartItem(updatedCart)
        } catch (error) {
            console.error('Error adding to cart:', error)
        } finally {
            setLoading(false)
        }
    }

    const removeLineItem = async (lineItemId) => {
        setLoading(true)
        console.warn('Removing line item')
        if (!cart?.id || !lineItemId) {
            console.warn("Missing cart ID or line item ID")
            return
        }
        try {
            const response = await shopify.request(`
                        mutation {
                          cartLinesRemove(cartId: "${cart.id}", lineIds: ["${lineItemId}"]) {
                            cart {
                              id
                              checkoutUrl
                              cost {
                                subtotalAmount {
                                  amount
                                  currencyCode
                                }
                                totalAmount {
                                  amount
                                  currencyCode
                                }
                              }
                              lines(first: 100) {
                                edges {
                                  node {
                                    id
                                    quantity
                                    cost {
                                        amountPerQuantity {
                                          amount
                                          currencyCode
                                        }
                                        totalAmount {
                                          amount
                                          currencyCode
                                        }
                                      }
                    
                                    merchandise {
                                      ... on ProductVariant {
                                        id
                                        title
                                        priceV2 {
                                            amount
                                            currencyCode
                                        }
                                        image {
                                          originalSrc
                                          altText
                                        }
                                        product {
                                          title
                                          handle
                                        }
                                      }
                                     }
                                  }
                                }
                              }
                            }
                          }
                        }
                      `)
            const updatedCart = response?.data?.cartLinesRemove?.cart

            if (!updatedCart) {
                console.error("❌ cartLinesRemove failed – no cart returned")
                return
            }

            setCartItem(updatedCart)
        } catch (error) {
            console.error('Error removing item:', error)
        } finally {
            setLoading(false)
        }
    }

    const updateLineItem = async (lineItemId, quantity) => {
        setLoading(true)
        try {
            const response = await shopify.request(`
                  mutation {
                    cartLinesUpdate(
                      cartId: "${cart.id}",
                      lines: [
                        {
                          id: "${lineItemId}",
                          quantity: ${quantity}
                        }
                      ]
                    ) {
                      cart {
                        id
                        checkoutUrl
                        cost {
                          subtotalAmount {
                            amount
                            currencyCode
                          }
                          totalAmount {
                            amount
                            currencyCode
                          }
                        }
                        lines(first: 100) {
                          edges {
                            node {
                              id
                              quantity
                              cost {
                                amountPerQuantity {
                                  amount
                                  currencyCode
                                }
                                totalAmount {
                                  amount
                                  currencyCode
                                }
                              }
                              merchandise {
                                ... on ProductVariant {
                                  id
                                  title
                                  priceV2 {
                                    amount
                                    currencyCode
                                  }
                                  image {
                                    originalSrc
                                    altText
                                  }
                                  product {
                                    title
                                    handle
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                `)

            const updatedCart = response?.data?.cartLinesUpdate?.cart
            if (updatedCart) {
                setCartItem(updatedCart)
            } else {
                console.warn("No updated cart returned after line update:", response)
            }
        } catch (error) {
            console.error('Error updating item:', error)
        } finally {
            setLoading(false)
        }
    }
    const setHiddenIds = (arr) => setHIds(arr)
    return (
        <StoreContext.Provider
            value={{
                cart,
                setCart,
                loading,
                addVariantToCart,
                removeLineItem,
                updateLineItem,
                hiddenIds,
                setHiddenIds,
            }}
        >
            {children}
        </StoreContext.Provider>
    )
}
